// Paper trades placed from the desk: where each stands (played out on real 5-minute candles by the
// API), with net P&L after MoneyHQ's cost estimates. Shown on the journal page, apart from real trades.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { NSE } from '@planb/core';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { openDesk } from './desk';
import { RowCard } from './row-card';

/** An edit made to a placed paper trade (applies from the next 5-minute candle on). */
export interface PaperChange { at: number; stop?: number; target?: number; qty?: number }

export interface PaperRow {
  id: string;
  trade: { symbol: string; side: 'long' | 'short'; entry: 'market' | 'limit' | 'stop'; price: number; stop: number; target: number; qty: number; horizon: 'intraday' | 'swing'; placedAt: number; trail?: string; now?: boolean; changes?: PaperChange[] };
  setup: string;
  createdAt: string;
  state: { status: string; final: boolean; fill: number | null; exit: number | null; last: number; net: number; r: number; stopNow?: number; held?: number;
    fillT: number | null; exitT: number | null; lastT: number; stopPath?: { date: string; stop: number }[] };
}

const px = NSE.formatPrice;
const inr = (v: number) => `${v < 0 ? '−' : ''}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const STATUS: Record<string, { label: string; cls: string }> = {
  waiting: { label: 'Waiting for entry', cls: 'bg-muted text-muted-foreground' },
  open: { label: 'Open', cls: 'bg-brand-soft text-brand-strong' },
  target: { label: 'Target hit', cls: 'bg-bull-soft text-bull' },
  stop: { label: 'Stopped out', cls: 'bg-bear-soft text-bear' },
  'squared-off': { label: 'Squared off 15:15', cls: 'bg-muted text-ink' },
  closed: { label: 'Closed by you', cls: 'bg-muted text-ink' },
  expired: { label: 'Expired unfilled', cls: 'bg-muted text-muted-foreground' },
  cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground' },
};
export const paperStatus = (s: string) => STATUS[s] ?? { label: s, cls: 'bg-muted' };
const ENTRY: Record<string, string> = { market: 'at market', limit: 'limit', stop: 'on a break of' };
const placed = (t: number) => new Date(t * 1000).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });

export function usePaperTrades() {
  const [rows, setRows] = useState<PaperRow[] | null>(null);
  const load = useCallback(() => api('/v1/paper').then((r) => (r.ok ? r.json() : [])).then(setRows).catch(() => setRows([])), []);
  useEffect(() => { void load(); const iv = setInterval(load, 60_000); return () => clearInterval(iv); }, [load]);
  return { rows, load };
}

/** What was changed and when, in a line ("stop → 1,150.00 at 11:42 · qty → 20 at 11:40"). */
function changesText(cs?: PaperChange[]) {
  if (!cs?.length) return '';
  return cs.map((c) => [c.stop != null && `stop → ${px(c.stop)}`, c.target != null && `target → ${px(c.target)}`, c.qty != null && `qty → ${c.qty}`].filter(Boolean).join(', ') + ` at ${new Date(c.at * 1000).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })}`).join(' · ');
}
/** The quantity after any change made before the fill. */
export const qtyOf = (t: PaperRow['trade']) => [...(t.changes ?? [])].reverse().find((c) => c.qty != null)?.qty ?? t.qty;
/** The stop and target in force now (the latest changes). */
export const levelsOf = (t: PaperRow['trade']) => ({
  stop: [...(t.changes ?? [])].reverse().find((c) => c.stop != null)?.stop ?? t.stop,
  target: [...(t.changes ?? [])].reverse().find((c) => c.target != null)?.target ?? t.target,
});

/** Edit an open or waiting paper trade: a new stop or target (from the next 5-minute candle on), or a
 * new quantity before it fills. The API checks each one and keeps it with its time. */
function EditPaper({ row, onClose, onSaved }: { row: PaperRow; onClose: () => void; onSaved: () => void }) {
  const t = row.trade, s = row.state, rule = t.trail === 'breakout';
  const cur = levelsOf(t);
  const [stop, setStop] = useState(String(rule ? (s.stopNow || t.stop) : cur.stop));
  const [target, setTarget] = useState(String(cur.target));
  const [qty, setQty] = useState(String(qtyOf(t)));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const body: Record<string, number> = {};
    const num = (v: string) => Number(v.replace(/[^\d.]/g, ''));
    if (num(stop) !== (rule ? (s.stopNow || t.stop) : cur.stop)) body.stop = num(stop);
    if (!rule && num(target) !== cur.target) body.target = num(target);
    if (s.fill == null && num(qty) !== qtyOf(t)) body.qty = Math.round(num(qty));
    if (!Object.keys(body).length) { onClose(); return; }
    setBusy(true);
    const r = await api(`/v1/paper/${row.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.error ?? 'Could not change it'); return; }
    toast.success('Changed: it counts from the next 5-minute candle');
    onSaved(); onClose();
  };
  const field = (label: string, v: string, set: (x: string) => void, hint?: string, disabled?: boolean) => (
    <label className="grid gap-1 text-[13px]">
      <span className="font-medium">{label}{hint && <span className="ml-1 font-normal text-muted-foreground">· {hint}</span>}</span>
      <Input inputMode="decimal" value={v} onChange={(e) => set(e.target.value)} disabled={disabled} className="num" />
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {t.symbol} paper trade</DialogTitle>
          <DialogDescription>Changes count from the next 5-minute candle, never backwards, and are kept with their time. R stays measured against the stop you placed it with.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {field('Stop ₹', stop, setStop, rule ? 'raise only: the rule’s trailing stop stays under it' : `was ${px(t.stop)}`)}
          {!rule && field('Target ₹', target, setTarget, `was ${px(t.target)}`)}
          {field('Quantity', qty, setQty, s.fill == null ? 'before it fills' : 'fixed once filled', s.fill != null)}
        </div>
        {t.changes?.length ? <p className="text-[12px] text-muted-foreground">Earlier changes: {changesText(t.changes)}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save change'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaperTrades() {
  const { rows, load } = usePaperTrades();
  const [editing, setEditing] = useState<PaperRow | null>(null);
  const canEdit = (r: PaperRow) => !r.state.final && r.trade.trail !== 'results';
  if (!rows || rows.length === 0) return null;
  const done = rows.filter((r) => r.state.final && r.state.fill != null);
  const wins = done.filter((r) => r.state.net > 0).length;
  const net = rows.reduce((s, r) => s + (r.state.fill != null ? r.state.net : 0), 0);
  const close = async (r: PaperRow) => {
    const res = await api(`/v1/paper/${r.id}/close`, { method: 'POST' });
    if (res.ok) { toast.success(r.state.status === 'waiting' ? 'Order cancelled' : 'Closed at the latest price'); void load(); }
    else toast.error('Could not close it');
  };
  return (
    <Card className="mt-6 gap-0 py-0">
      <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-2 border-b py-4">
        <div>
          <CardTitle className="text-[15px] font-semibold">Paper trades</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">Placed from the desk and played out on real 5-minute candles, after MoneyHQ’s cost estimates. Kept apart from your real journal.</p>
        </div>
        <p className="num text-[13px] text-muted-foreground">
          {done.length > 0 && <>{wins}/{done.length} closed in profit · </>}net <b className={cn(net >= 0 ? 'text-bull' : 'text-bear')}>{inr(net)}</b>
        </p>
      </CardHeader>
      {/* Phones: one card per trade. */}
      <div className="grid gap-2 p-3 sm:hidden">
        {rows.map((r) => {
          const t = r.trade, s = r.state, st = paperStatus(s.status);
          return (
            <RowCard key={r.id}
              title={<>{t.symbol} <span className={cn('ml-1 text-[11px] font-medium', t.side === 'long' ? 'text-bull' : 'text-bear')}>{t.side}</span></>}
              sub={<>{r.setup || '—'} · {t.horizon} · {placed(t.placedAt)}</>}
              badge={<span className={cn('inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-semibold', st.cls)}>{st.label}</span>}
              cells={[
                { label: 'Entry', value: s.fill != null ? <>{px(s.fill)}{t.now && <span className="block text-[11px] text-muted-foreground">entered now (untested timing)</span>}</> : t.now ? 'now, next 5-min candle' : t.trail ? 'next open' : `${ENTRY[t.entry]} ${t.entry === 'market' ? '' : px(t.price)}` },
                ...(s.exit != null ? [{ label: 'Exit', value: px(s.exit) }] : []),
                { label: 'Stop', value: t.trail === 'results' ? 'none' : px(t.trail ? s.stopNow || t.stop : levelsOf(t).stop) },
                ...(!t.trail ? [{ label: 'Target', value: px(levelsOf(t).target) }] : []),
                { label: 'Qty', value: qtyOf(t).toLocaleString('en-IN') },
                { label: s.final ? 'Net' : 'Net now', value: s.fill == null ? '—' : inr(s.net), className: s.fill == null ? undefined : s.net >= 0 ? 'text-bull' : 'text-bear' },
                { label: 'R', value: s.fill == null ? '—' : s.r.toFixed(2) },
              ]}
              actions={(s.fill != null || s.status === 'expired' || !s.final) ? <>
                {s.fill != null && <Button variant="ghost" size="sm" className="h-8 text-[12.5px] text-brand-strong" onClick={() => openDesk(`Review my ${t.symbol} paper trade (ref ${r.id.slice(0, 8)})`)}>Review</Button>}
                {s.status === 'expired' && <Button variant="ghost" size="sm" className="h-8 text-[12.5px] text-brand-strong" onClick={() => openDesk(`Why didn't my ${t.symbol} paper order fill? (ref ${r.id.slice(0, 8)})`)}>Why?</Button>}
                {canEdit(r) && <Button variant="ghost" size="sm" className="h-8 text-[12.5px]" onClick={() => setEditing(r)}>Edit</Button>}
                {!s.final && <Button variant="outline" size="sm" className="h-8 rounded-full text-[12.5px]" onClick={() => void close(r)}>{s.status === 'waiting' ? 'Cancel' : 'Close'}</Button>}
                {t.changes?.length ? <p className="w-full text-[11px] text-muted-foreground">Changed: {changesText(t.changes)}</p> : null}
              </> : undefined} />
          );
        })}
      </div>
      <CardContent className="overflow-x-auto p-0 max-sm:hidden">
        <table className="w-full min-w-[760px] text-[13px]">
          <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase">
            <tr className="border-b">{['Stock', 'Plan', 'Placed', 'Status', 'Entry', 'Stop · target', 'Qty', 'Net', 'R', ''].map((h) => <th key={h} className="px-3 py-2 font-medium first:pl-5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const t = r.trade, s = r.state, st = paperStatus(s.status);
              return (
                <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2.5 pl-5 font-semibold">{t.symbol} <span className={cn('ml-1 text-[11px] font-medium', t.side === 'long' ? 'text-bull' : 'text-bear')}>{t.side}</span></td>
                  <td className="px-3 py-2.5 text-muted-foreground">{r.setup || '—'} <span className="text-[11px]">· {t.horizon}</span></td>
                  <td className="num px-3 py-2.5 text-muted-foreground">{placed(t.placedAt)}</td>
                  <td className="px-3 py-2.5"><span className={cn('inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold', st.cls)}>{st.label}</span></td>
                  <td className="num px-3 py-2.5">{s.fill != null ? px(s.fill) : t.now ? 'now, next 5-min candle' : t.trail ? 'next open' : `${ENTRY[t.entry]} ${t.entry === 'market' ? '' : px(t.price)}`}{s.exit != null && <span className="text-muted-foreground"> → {px(s.exit)}</span>}{t.now && <span className="block font-sans text-[11px] text-muted-foreground" title="Filled at the next 5-minute candle after placing, not the next session's open: a timing the rule's tested record doesn't cover">entered now · untested timing</span>}</td>
                  <td className="num px-3 py-2.5 text-muted-foreground">{t.trail === 'results' ? <>held 20 sessions · no stop</> : t.trail ? <>trailing {px(s.stopNow || t.stop)}</> : <>{px(levelsOf(t).stop)} · {px(levelsOf(t).target)}</>}{t.changes?.length ? <span className="block font-sans text-[11px]" title={changesText(t.changes)}>changed {t.changes.length}×</span> : null}</td>
                  <td className="num px-3 py-2.5">{qtyOf(t).toLocaleString('en-IN')}</td>
                  <td className={cn('num px-3 py-2.5 font-medium', s.fill == null ? 'text-muted-foreground' : s.net >= 0 ? 'text-bull' : 'text-bear')}>{s.fill == null ? '—' : inr(s.net)}{s.fill != null && !s.final && <span className="text-[10px] font-normal text-muted-foreground"> now</span>}</td>
                  <td className="num px-3 py-2.5">{s.fill == null ? '—' : s.r.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {s.fill != null && <Button variant="ghost" size="sm" className="h-7 text-[12px] text-brand-strong" title="The desk walks you through this trade on the real candles"
                      onClick={() => openDesk(`Review my ${t.symbol} paper trade (ref ${r.id.slice(0, 8)})`)}>Review</Button>}
                    {s.status === 'expired' && <Button variant="ghost" size="sm" className="h-7 text-[12px] text-brand-strong" onClick={() => openDesk(`Why didn't my ${t.symbol} paper order fill? (ref ${r.id.slice(0, 8)})`)}>Why?</Button>}
                    {canEdit(r) && <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => setEditing(r)}>Edit</Button>}
                    {!s.final && <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => void close(r)}>{s.status === 'waiting' ? 'Cancel' : 'Close'}</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
      {editing && <EditPaper row={editing} onClose={() => setEditing(null)} onSaved={() => void load()} />}
    </Card>
  );
}
